import { queryOptions, type QueryClient } from "@tanstack/react-query"
import { api } from "./api"
import { isV2Api } from "./api-contract"
import { getSessionUser } from "./auth"
import type { ChatMessage } from "./channels"
import type { MessageReactionTally } from "./message-view"
import {
  createEventConversation,
  getMyV2Profile,
  listAllEventPeople,
  listEventConversationMessages,
  listEventConversations,
  markEventConversationRead,
} from "./v2/organizer"
import type { V2ConversationSummary } from "./v2/contracts"
import { v2MessageToChatMessage } from "./channels"

// Direct-message (1:1 + group DM) data layer. DMs are `conversations` rows with
// scope='conversation' + explicit conversation_members. Unlike channels, the DM
// list is sourced from the purpose-built `list-my-conversations` action (the
// raw CRUD `/conversations` list is firewalled per-tenant and doesn't compute
// participants / unread), then filtered to scope='conversation' here.
//
// CRITICAL — cache keys. A DM's MESSAGE stream reuses the exact channels key
// `["resource", "chat-messages", { conversationId }]` so the vendored realtime
// layer (realtime.ts → tryMessageInsertSplice) splices live chat-messages
// INSERTs into the open DM with zero extra wiring. The DM LIST has its own key
// (it isn't a plain `["resource", ...]` list — it's the action's bundle), so it
// is invalidated on send / create rather than CRUD-patched.

export type ConversationParticipant = {
  userId: string
  displayName: string
  avatarImageId: string | null
  handle: string | null
  role: string
  /**
   * True when this participant is no longer an active member of the org/event
   * (deactivated or removed). Optional + defaults to active: until the backend
   * projects it, the value is undefined and the UI behaves exactly as before
   * (no badge, compose enabled). See the deactivation helpers below.
   */
  deactivated?: boolean
}

export type ConversationLastMessage = {
  id: string
  authorName: string
  authorRole: string | null
  body: string | null
  kind: string
  createdAt: string
}

// One inbox row as `list-my-conversations` / `create-conversation` emit it.
export type ConversationItem = {
  id: string
  eventId: string
  scope: "channel" | "conversation"
  name: string | null
  topic: string | null
  emoji: string | null
  agendaItemId: string | null
  agendaGroupId: string | null
  attendeeGroupId?: string | null
  includesAllConfirmedGuests: boolean
  pinnedAtTop: boolean
  archivedAt: string | null
  postingMode: string
  canPost: boolean
  participants: ConversationParticipant[]
  lastMessage: ConversationLastMessage | null
  unread: number
}

// One row from the DM recipient picker (`list-roster` with forDm=true).
export type RosterPerson = {
  /** Canonical person ID in v2; Better Auth user ID in the v1 adapter. */
  recipientId: string
  handle: string | null
  displayName: string
  avatarImageId: string | null
  canDm: boolean
}

type ConversationsEnvelope = { success: true; conversations: ConversationItem[] }
export type MessagesEnvelope = {
  success: true
  data: ChatMessage[]
  nextCursor: string | null
  hasMore: boolean
  // Grouped reactions for the returned messages, keyed by message id — bundled
  // by list-conversation-messages so the client seeds useReactions in one pass.
  reactions?: Record<string, MessageReactionTally[]>
}
// ── query keys ────────────────────────────────────────────────────────────────
// The DM list is per-event; keyed by event so a workspace switch refetches.
export const conversationsKey = (eventId: string) =>
  ["dm-conversations", eventId] as const
// Reuses the channels message-stream key so realtime splices flow through.
export const conversationMessagesKey = (conversationId: string) =>
  ["resource", "chat-messages", { conversationId }] as const
export const dmRosterKey = (eventId: string) => ["dm-roster", eventId] as const

// ── query options ────────────────────────────────────────────────────────────
export function conversationsQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: conversationsKey(eventId),
    queryFn: () => isV2Api
      ? listEventConversations(eventId).then((response): ConversationsEnvelope => ({
          success: true,
          conversations: response.conversations.map((conversation) =>
            v2ConversationToItem(conversation, response.viewer.personId)),
        }))
      : api<ConversationsEnvelope>(
        `/api/v1/event/${eventId}/chat/list-my-conversations`,
        { method: "POST", body: {} },
      ),
    // Only DMs / group DMs — channels have their own surface.
    select: (r) => r.conversations.filter((c) => c.scope === "conversation"),
  })
}

export function conversationMessagesQueryOptions(
  eventId: string,
  conversationId: string,
) {
  return queryOptions({
    queryKey: conversationMessagesKey(conversationId),
    queryFn: () => isV2Api
      ? listEventConversationMessages({ eventId, conversationId }).then(
          (response): MessagesEnvelope => ({
            success: true,
            data: response.messages.map(v2MessageToChatMessage).reverse(),
            nextCursor: response.page.nextBeforeSequence === null
              ? null
              : String(response.page.nextBeforeSequence),
            hasMore: response.page.hasMore,
            reactions: v2ReactionSeed(response),
          }),
        )
      : api<MessagesEnvelope>(
        `/api/v1/event/${eventId}/chat/list-conversation-messages`,
        { method: "POST", body: { conversationId, limit: 100 } },
      ),
    // Shape to match the channels stream envelope ({ data }) so the shared
    // MessageStream + realtime splice (which read `.data`) work unchanged.
    select: (r): MessagesEnvelope => r,
  })
}

export function fetchConversationMessagesPage(
  eventId: string,
  conversationId: string,
  before: string | null,
  limit = 100,
) {
  if (isV2Api) {
    const beforeSequence = before === null ? null : Number(before)
    return listEventConversationMessages({
      eventId,
      conversationId,
      limit,
      beforeSequence: Number.isFinite(beforeSequence) ? beforeSequence : null,
    }).then((response): MessagesEnvelope => ({
      success: true,
      data: response.messages.map(v2MessageToChatMessage).reverse(),
      nextCursor: response.page.nextBeforeSequence === null
        ? null
        : String(response.page.nextBeforeSequence),
      hasMore: response.page.hasMore,
      reactions: v2ReactionSeed(response),
    }))
  }
  return api<MessagesEnvelope>(
    `/api/v1/event/${eventId}/chat/list-conversation-messages`,
    {
      method: "POST",
      body: {
        conversationId,
        limit,
        ...(before ? { before } : {}),
      },
    },
  )
}

export function dmRosterQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: dmRosterKey(eventId),
    queryFn: async () => {
      if (isV2Api) {
        const [viewer, people] = await Promise.all([
          getMyV2Profile(),
          listAllEventPeople(eventId),
        ])
        return {
          success: true as const,
          roster: people
            .filter((person) =>
              person.personId !== viewer.id &&
              person.status === "active" &&
              person.roles.some((role) => role.status === "active" && role.kind === "staff"),
            )
            .map((person): RosterPerson => ({
              recipientId: person.personId,
              handle: null,
              displayName: person.displayName,
              avatarImageId: null,
              canDm: true,
            })),
        }
      }
      const response = await api<{
        success: true
        roster: Array<Omit<RosterPerson, "recipientId"> & { userId: string }>
      }>(`/api/v1/event/${eventId}/chat/list-roster`, {
        method: "POST",
        body: { forDm: true },
      })
      return {
        success: true as const,
        roster: response.roster.map(({ userId, ...person }) => ({
          ...person,
          recipientId: userId,
        })),
      }
    },
    select: (r) => r.roster,
  })
}

// ── mutations ────────────────────────────────────────────────────────────────
export type CreateConversationResult = {
  success: true
  committed: boolean
  deduped?: boolean
  conversation: ConversationItem
}

// Open (or, for an existing 1:1, return) a DM with one or more recipients. The
// backend dedups 1:1 threads and enforces opt-out / block gates (staff bypass).
export async function createConversation(input: {
  eventId: string
  recipientIds: string[]
  title?: string
}): Promise<CreateConversationResult> {
  if (isV2Api) {
    const response = await createEventConversation({
      eventId: input.eventId,
      kind: "direct",
      title: input.title?.trim() || "Direct conversation",
      personIds: input.recipientIds,
    })
    return {
      success: true,
      committed: true,
      conversation: v2ConversationToItem({
        ...response.conversation,
        memberCount: input.recipientIds.length + 1,
        unreadCount: 0,
        lastMessage: null,
      }),
    }
  }
  return api<CreateConversationResult>(
    `/api/v1/event/${input.eventId}/chat/create-conversation`,
    {
      method: "POST",
      body: { recipientUserIds: input.recipientIds, dryRun: false },
    },
  )
}

// Mark a DM read — idempotent server upsert of the read pointer. Shares the
// same action channels use, so this is best-effort fire-and-forget.
export async function markConversationRead(
  eventId: string,
  conversationId: string,
): Promise<void> {
  if (isV2Api) {
    await markEventConversationRead(eventId, conversationId)
    return
  }
  await api(`/api/v1/event/${eventId}/chat/mark-conversation-read`, {
    method: "POST",
    body: { conversationId },
  })
}

// Drop the unread badge of one conversation in the (action-sourced) list cache
// without a refetch — used on open. Returns true when a row was patched.
export function clearConversationUnread(
  qc: QueryClient,
  eventId: string,
  conversationId: string,
): boolean {
  let patched = false
  qc.setQueryData<ConversationsEnvelope>(conversationsKey(eventId), (old) => {
    if (!old) return old
    const next = old.conversations.map((c) => {
      if (c.id !== conversationId || c.unread === 0) return c
      patched = true
      return { ...c, unread: 0 }
    })
    return patched ? { ...old, conversations: next } : old
  })
  return patched
}

// ── display helpers ───────────────────────────────────────────────────────────

// A DM title is the comma-joined OTHER participants (the signed-in user is
// dropped). A solo thread (note-to-self) falls back to "(you)".
export function dmTitle(
  c: ConversationItem,
  currentUserId: string | null,
): string {
  const others = otherParticipants(c, currentUserId)
  if (others.length === 0) return c.name ?? "Conversation"
  return others.map((p) => p.displayName).join(", ")
}

export function v2ConversationToItem(
  conversation: V2ConversationSummary,
  viewerPersonId?: string,
): ConversationItem {
  return {
    id: conversation.id,
    eventId: conversation.eventId,
    scope: conversation.kind === "direct" ? "conversation" : "channel",
    name: conversation.title,
    topic: conversation.topic,
    emoji: conversation.emoji ?? null,
    agendaItemId:
      conversation.originScopeKind === "activity" ? conversation.originScopeId : null,
    agendaGroupId: conversation.choiceGroupIds?.[0] ?? null,
    attendeeGroupId: conversation.segmentIds?.[0] ?? null,
    includesAllConfirmedGuests: conversation.includeAllConfirmedAttendees ?? false,
    pinnedAtTop: conversation.pinned ?? false,
    archivedAt: conversation.status === "archived" ? conversation.closedAt : null,
    postingMode: conversation.postingMode ??
      (conversation.kind === "announcement" ? "organizers-only" : "members"),
    canPost: conversation.canPost ?? conversation.status === "active",
    participants: (conversation.members ?? []).map((member) => ({
      userId:
        member.personId === viewerPersonId
          ? getSessionUser()?.id ?? member.personId
          : member.personId,
      displayName: member.displayName,
      avatarImageId: member.avatarImageId,
      handle: member.handle,
      role: member.role,
      deactivated: member.deactivated,
    })),
    lastMessage: conversation.lastMessage
      ? {
          id: conversation.lastMessage.id,
          authorName: conversation.lastMessage.authorDisplayName ?? "Event Team",
          authorRole: conversation.lastMessage.authorRole,
          body: conversation.lastMessage.body,
          kind: conversation.lastMessage.messageKind ?? "person",
          createdAt: conversation.lastMessage.sentAt,
        }
      : null,
    unread: conversation.unreadCount,
  }
}

function emptyReactionSeed(
  messages: Array<{ id: string }>,
): Record<string, MessageReactionTally[]> {
  return Object.fromEntries(messages.map((message) => [message.id, []]))
}

function v2ReactionSeed(
  response: import("./v2/contracts").V2ConversationMessagesPage,
): Record<string, MessageReactionTally[]> {
  if (!response.reactions) return emptyReactionSeed(response.messages)
  return Object.fromEntries(
    Object.entries(response.reactions).map(([messageId, tallies]) => [
      messageId,
      tallies.map((tally) => ({
        emoji: tally.emoji,
        count: tally.count,
        mine: tally.mine,
        reactorUserIds: tally.reactorPersonIds,
      })),
    ]),
  )
}

export function otherParticipants(
  c: ConversationItem,
  currentUserId: string | null,
): ConversationParticipant[] {
  const others = c.participants.filter((p) => p.userId !== currentUserId)
  return others.length > 0 ? others : c.participants
}

export function isGroupDm(
  c: ConversationItem,
  currentUserId: string | null,
): boolean {
  return otherParticipants(c, currentUserId).length > 1
}

// The OTHER participants that are no longer active org members. Never includes
// the signed-in user. Empty until the backend projects `deactivated`.
export function deactivatedParticipants(
  c: ConversationItem,
  currentUserId: string | null,
): ConversationParticipant[] {
  return otherParticipants(c, currentUserId).filter((p) => p.deactivated === true)
}

// True only for a 1:1 DM whose sole other participant is deactivated — the case
// where we disable the composer (in a group, others may still be active).
export function isDmRecipientDeactivated(
  c: ConversationItem,
  currentUserId: string | null,
): boolean {
  const others = otherParticipants(c, currentUserId)
  return others.length === 1 && others[0]!.deactivated === true
}

// Deterministic avatar gradient per user so the same person reads the same
// across the rail + thread (mirrors the mockup's coloured initials chips).
const AVATAR_GRADIENTS = [
  "from-sky-400 to-indigo-500",
  "from-amber-300 to-orange-500",
  "from-emerald-400 to-teal-600",
  "from-pink-400 to-rose-600",
  "from-violet-400 to-fuchsia-600",
  "from-teal-400 to-cyan-600",
  "from-rose-400 to-red-500",
  "from-lime-400 to-green-600",
] as const

export function avatarGradient(userId: string): string {
  let h = 0
  for (let i = 0; i < userId.length; i++) h = (h * 31 + userId.charCodeAt(i)) >>> 0
  return AVATAR_GRADIENTS[h % AVATAR_GRADIENTS.length]
}

export function personInitials(name: string): string {
  return (
    name
      .split(/\s+/)
      .map((p) => p[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?"
  )
}

// Flatten a last-message preview for the rail. Strips whitespace; falls back to
// a kind-aware label for non-text messages (image-only, system).
export function previewText(last: ConversationLastMessage | null): string {
  if (!last) return "No messages yet"
  const body = (last.body ?? "").replace(/\s+/g, " ").trim()
  if (body) return body
  if (last.kind === "image" || last.kind === "attachment") return "Sent an attachment"
  if (last.kind === "system") return body || "—"
  return "Sent a message"
}
